import { EventHint } from '@sentry/react';
import {
    isSafari,
    isFeiShuMobile,
    isFeiShuDesktop,
    isDingdingDesktop,
    isFeishu,
    isHeyTapBrowser,
    isMobileLenovoBrowser,
    isPinganZhiniaoWebview,
    isQQBrowser,
    isEdge,
    isUMUWebview,
    isWxChat,
    isDesktop,
    isDingding,
    isPixel,
    isSharp,
    isSony,
} from '@umu/umu-ua';

const currentJsContext: Record<string, boolean> = {
    'umuapp': isUMUWebview,
    'desktop': isDesktop,
    'dingdingdesktop': isDingdingDesktop,
    'wxchat': isWxChat,
    'dingding': isDingding,
    'feishu-mobile': isFeiShuMobile,
    'qqbrowser': isQQBrowser,
    'edge': isEdge,
    'safari': isSafari,
    'heytap': isHeyTapBrowser,
    'mobile-lenovo': isMobileLenovoBrowser,
    'pingan-zhiniao': isPinganZhiniaoWebview,
    'qq': isQQBrowser,
    'feishu': isFeishu,
    'feishu-desktop': isFeiShuDesktop,
    'pixel': isPixel,
    'sharp': isSharp,
    'sony': isSony,
};

export const getJsContext = () => {
    for (const key in currentJsContext) {
        if (currentJsContext[key]) {
            return key;
        }
    }
    return 'browser';
};

/**
 * URL 归一化处理，将动态ID替换为占位符
 * 用于减少 Sentry 中相似错误的分组数量
 */
const normalizeUrl = (url: string): string => {
    // 类型安全检查：只处理非空字符串
    if (!url || typeof url !== 'string') {
        return url;
    }

    // 定义需要归一化的路径模式
    // 注意：更具体的模式应该放在更通用的模式之前，避免被提前匹配
    const patterns = [
        // /user/assigngroup/{id}
        { regex: /\/user\/assigngroup\/[a-zA-Z0-9]+/g, replacement: '/user/assigngroup/:id' },
        // /program/rank/{programId} - 学习项目排行榜（必须在 /program/{id} 之前）
        { regex: /\/program\/rank\/[a-zA-Z0-9]+/g, replacement: '/program/rank/:programId' },
        // /program/{id} - 使用负向前瞻排除 rank
        { regex: /\/program\/(?!rank(?:\/|$))[a-zA-Z0-9]+/g, replacement: '/program/:id' },
        // /class/home/{id} - 班级首页（必须在 /class/{id} 之前）
        { regex: /\/class\/home\/[a-zA-Z0-9]+/g, replacement: '/class/home/:id' },
        // /class/{id} - 使用负向前瞻排除 index 和 home
        { regex: /\/class\/(?!(?:index|home)(?:\/|$))[a-zA-Z0-9]+/g, replacement: '/class/:id' },
        // /session/ask/{id}
        { regex: /\/session\/ask\/[a-zA-Z0-9]+/g, replacement: '/session/ask/:id' },
        // /session/survey/{id}
        { regex: /\/session\/survey\/[a-zA-Z0-9]+/g, replacement: '/session/survey/:id' },
        // /session/aiaudioslides/{id}
        { regex: /\/session\/aiaudioslides\/[a-zA-Z0-9]+/g, replacement: '/session/aiaudioslides/:id' },
        // /session/sign/{id}
        { regex: /\/session\/sign\/[a-zA-Z0-9]+/g, replacement: '/session/sign/:id' },
        // /session/discuss/{id}
        { regex: /\/session\/discuss\/[a-zA-Z0-9]+/g, replacement: '/session/discuss/:id' },
        // /session/article/{id}
        { regex: /\/session\/article\/[a-zA-Z0-9]+/g, replacement: '/session/article/:id' },
        // /session/document/{id}
        { regex: /\/session\/document\/[a-zA-Z0-9]+/g, replacement: '/session/document/:id' },
        // /session/quiz/{id}
        { regex: /\/session\/quiz\/[a-zA-Z0-9]+/g, replacement: '/session/quiz/:id' },
        // /session/video/{id} 和 /session/video/{id}/xxx
        { regex: /\/session\/video\/[a-zA-Z0-9]+/g, replacement: '/session/video/:id' },
        // /session/exercise/{id}
        { regex: /\/session\/exercise\/[a-zA-Z0-9]+/g, replacement: '/session/exercise/:id' },
        // /session/game/{id}
        { regex: /\/session\/game\/[a-zA-Z0-9]+/g, replacement: '/session/game/:id' },
        // /session/infographicContent/{id}
        { regex: /\/session\/infographicContent\/[a-zA-Z0-9]+/g, replacement: '/session/infographicContent/:id' },
        // /session/live/{id} 和 /session/live/{id}/edit
        { regex: /\/session\/live\/[a-zA-Z0-9]+/g, replacement: '/session/live/:id' },
        // /session/lottery/{id}
        { regex: /\/session\/lottery\/[a-zA-Z0-9]+/g, replacement: '/session/lottery/:id' },
        // /session/phase-evaluation-mentor/{id}
        {
            regex: /\/session\/phase-evaluation-mentor\/[a-zA-Z0-9]+/g,
            replacement: '/session/phase-evaluation-mentor/:id',
        },
        // /session/phase-evaluation/{id}
        { regex: /\/session\/phase-evaluation\/[a-zA-Z0-9]+/g, replacement: '/session/phase-evaluation/:id' },
        { regex: /\/session\/photo\/[a-zA-Z0-9]+/g, replacement: '/session/photo/:id' },
        { regex: /\/session\/aiPrompt\/[a-zA-Z0-9]+/g, replacement: '/session/aiPrompt/:id' },
        // /session/audioslides/{id} - 微课
        { regex: /\/session\/audioslides\/[a-zA-Z0-9-]+/g, replacement: '/session/audioslides/:id' },
        // /session/infographic/{id}
        { regex: /\/session\/infographic\/[a-zA-Z0-9-]+/g, replacement: '/session/infographic/:id' },
        // /element/share/{id} - 小节分享
        { regex: /\/element\/share\/[a-zA-Z0-9]+/g, replacement: '/element/share/:id' },
        // /element/survey_result_share/{id} - 问卷结果分享
        { regex: /\/element\/survey_result_share\/[a-zA-Z0-9]+/g, replacement: '/element/survey_result_share/:id' },
        // /element/quiz_result_share/{id} - 考试答案分享
        { regex: /\/element\/quiz_result_share\/[a-zA-Z0-9]+/g, replacement: '/element/quiz_result_share/:id' },
        // /exercise/feedback/{id} - 作业过程点评
        { regex: /\/exercise\/feedback\/[a-zA-Z0-9]+/g, replacement: '/exercise/feedback/:id' },
        // /homework/feedback/{id} - 作业过程点评
        { regex: /\/homework\/feedback\/[a-zA-Z0-9]+/g, replacement: '/homework/feedback/:id' },
        // /exercise/evaluate/{id} - 作业评价
        { regex: /\/exercise\/evaluate\/[a-zA-Z0-9]+/g, replacement: '/exercise/evaluate/:id' },
        // /homework/evaluate/{id} - 作业评价
        { regex: /\/homework\/evaluate\/[a-zA-Z0-9]+/g, replacement: '/homework/evaluate/:id' },
        // /access-denied 相关路由
        // 保留固定的类型: access-time-limit, ip-limit
        // 但是 business/* 和其他动态参数需要归一化
        // 注意：这里的顺序很重要，需要先匹配 business/xxx，再匹配其他动态参数
        // /access-denied/business/{xxx} - 归一化为 /access-denied/business/:param
        { regex: /\/access-denied\/business\/[a-zA-Z0-9+/=_-]+/g, replacement: '/access-denied/business/:param' },
        // /access-denied/{动态参数} - 但排除 access-time-limit 和 ip-limit
        // 使用负向前瞻 (?!...) 排除固定类型，(?:$|[?/#]) 确保匹配到路径结束、查询参数或锚点
        {
            regex: /\/access-denied\/(?!(?:access-time-limit|ip-limit)(?:$|[?/#])|business\/)[a-zA-Z0-9+/=_-]+/g,
            replacement: '/access-denied/:param',
        },
    ];

    let normalizedUrl = url;
    for (const pattern of patterns) {
        normalizedUrl = normalizedUrl.replace(pattern.regex, pattern.replacement);
    }

    return normalizedUrl;
};

// 临时忽略错误 - 音视频相关的报错
const IgnoreVideoErrorsList = [
    'NotSupportedError: The element has no supported sources.',
    'The operation is not supported.',
    'NotAllowedError: Permission dismissed',
    'NotAllowedError: Permission denied',
    'NotReadableError: Could not start audio source',
    'NotSupportedError: Failed to load because no supported source was found.',
    "NotAllowedError: play() failed because the user didn't interact with the document first. https://goo.gl/xX8pDD",
    'NotReadableError: Could not start video source',
    'NotFoundError: Requested device not found',
    'NotSupportedError: Not supported',
];

// 临时忽略基础错误
const IgnoreBaseErrorsList = ['请求超时。'];

let isPageReloading = false;

window.addEventListener('beforeunload', () => {
    isPageReloading = true;
});
window.addEventListener('pagehide', () => {
    isPageReloading = true;
});

// get ignore errors list
export const getIgnoreErrorsList = () => {
    return [
        'window.ttJSBridge is undefined', // 飞书
        'Java object is gone',
        "Can't find variable: WeakRef", // 微信小程序内报错，先忽略
        "Can't find variable: lk_isInPIP",
        "Can't find variable: zaloJSV2",
        "Can't find variable: lk_pauseAllMedia",
        'zaloJSV2 is not defined',
        "Can't find variable: lk_pauseAllMedia",
        'handler is not a function',
        'WebKit 遇到内部错误',
        'The video element is not ready to enter the Picture-in-Picture mode.',
        'InvalidStateError: The object is in an invalid state.',
        'LIDNotify is not defined',
        "Can't find variable: UCReadabilityContext",
        'UCShellJava.sdkEventFire is not a function', // uc 浏览器错误
        'https://statics-cdn-cn.umucdn.cn/statics/js/pdf/pdf.min.2.12.js', // pdf 加载失败，一般是切换页面导致的
        'InvalidStateError: The object is in an invalid state.', // video内部播放错误
        'NotAllowedError: play() can only be initiated by a user gesture.', // video 播放错误
        'err null',
        'domtoimage: Error while reading CSS rules from',
        'Member PerformanceObserverInit.entryTypes is required and must be an instance of sequence',
        'SMGNativeJS is not defined',
        'onConfigurationInit is not defined',
        'The video element is processing a Picture-in-Picture request',
        'cdn.huiju.cool',
        '(error: https://cdn.huiju.cool/js/20240415/clab_chunk.js)',
        '[KEKEAD str]', //https://app.asana.com/0/51340828583966/1209726452453875/f
        '__WeixinJSBridgeNativeProxy__ not found',
        'do not belong to channel host',
        'makeWeikeStatusErrorLog', // https://app.asana.com/0/0/1209725751413595/f
        'LIDNotifyId is not defined',
        'Setting up fake worker failed', // https://app.asana.com/0/0/1209711855434494/f
        'WeixinJSBridge',
        '[video-takeover-extract]',
        'empty host rule',
        "Can't find variable: WebViewJavascriptBridge",
        'WebViewJavascriptBridge is not defined',
        "Can't find variable: WeixinJSBridge",
        "Can't find variable: LarkWebViewJavaScriptBridge",
        'AbortError: The operation was aborted.',
        'Load failed',
        'TypeError: Load failed',
        'Failed to fetch',
        'TypeError: Failed to fetch',
        'No error message',
        'The network connection was lost.',
        "Can't find variable: cordova",
        'appcb is not defined',
        '網路連線中斷。',
        '网络连接已中断',
        '已取消',
        'Internet連線已斷開。',
        /^AbortError: The play/i,
        /^NotAllowedError: The request is not/i,
        /^The fetching process for the media resource was aborted/i,
        /Unexpected identifier '.*[\u4e00-\u9fff]+.*'/i, // 忽略包含中文字符的语法错误，通常是用户输入或攻击尝试
        'ChunkLoadError',
        'The above error occurred in task',
        'Please use the NodeViewWrapper component for your node view',
        'Java bridge method invocation error',
        'LanxinJsBridge is not defined',
        "Can't find variable: LanxinJsBridge",
        'Internal error when calculating storage usage',
        'A network error occurred',
        'UC_shouldChangeTheme'
    ]
        .concat(IgnoreVideoErrorsList)
        .concat(IgnoreBaseErrorsList);
};

export const innerBeforeSend = (event: any, _hint: EventHint, dynamicSentryIgnoreErrors: string[]) => {
    // URL 归一化处理
    if (event?.request?.url) {
        event.request.url = normalizeUrl(event.request.url);
    }
    if (event?.transaction) {
        event.transaction = normalizeUrl(event.transaction);
    }
    // 归一化 breadcrumbs 中的 URL
    if (event?.breadcrumbs && Array.isArray(event.breadcrumbs)) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb: any) => {
            if (!breadcrumb?.data) {
                return breadcrumb;
            }

            // 创建新的 data 对象，归一化所有可能包含 URL 的属性
            const normalizedData = { ...breadcrumb.data };

            if (normalizedData.url) {
                normalizedData.url = normalizeUrl(normalizedData.url);
            }
            if (normalizedData.to) {
                normalizedData.to = normalizeUrl(normalizedData.to);
            }
            if (normalizedData.from) {
                normalizedData.from = normalizeUrl(normalizedData.from);
            }

            return {
                ...breadcrumb,
                data: normalizedData,
            };
        });
    }

    // filter out UnhandledRejection errors that have no information https://github.com/getsentry/sentry-javascript/issues/3440
    const ignoreErrorList = [
        'Please use the NodeViewWrapper component for your node view.',
        "'get' on proxy: property 'javaEnabled' is a read-only",
        'Loading chunk 1 failed.',
        "Can't find variable: Aplus4Native",
        "Can't find variable: DarkReader",
        'キャンセルしました',
        'missing ) after argument list',
        'The above error occurred in task', // saga报错，和 Load failed 是级联问题
        // 'hmErrorLog2', // 日志机制，需要从日志处去掉
        'Java bridge method invocation error',
        "undefined is not an object (evaluating 'window.webkit.messageHandlers')",
        'window.weixinPostMessageHandlers.weixinDispatchMessage.postMessage',
        "Failed to execute 'sendBeacon' on 'Navigator'",
        "Failed to execute 'replaceState' on 'History'",
        'Invalid PDF structure.',
        "Can't find variable: QK_middlewareReadModePageDetect",
        'nativeInitedNotify is not defined',
        '[video-takeover] window.BrowserStat.generalStat',
        // https://sentry.umu.cn/organizations/sentry/issues/64577453/?project=11&query=is%3Aunresolved&referrer=issue-stream
        // 原因：课程获取学习证书时会有一个撒花特效，是这个 react-canvas-confetti 库提供的，它里面会通过 webWorker 来绘制，应该是它内部逻辑的问题，先忽略
        'e.getBoundingClientRect is not a function',
        // https://sentry.umu.cn/organizations/sentry/issues/213048714/?project=8&project=11&project=20&query=is%3Aunresolved&referrer=issue-stream&sort=user&statsPeriod=14d
        // 过程点评获取麦克风权限报错，这个需要用户主动授权，页面已经有比较好的提示了，先忽略
        '[timeline] [useRecorder] getUserMedia error NotAllowedError: Permission dismissed',
        // https://app.asana.com/1/45778247595893/project/1200987305187523/task/1210094969855292?focus=true
        // 来自于腾讯云文档：由于视频文件损坏或是该视频使用了你的浏览器不支持的功能，播放终止。
        // 我们没啥好解决的，直接在代码 sentry 忽略吧
        'he media playback was aborted due to a corruption problem or because the media used features your browser did not support',
        // TCPlayer 的报错先忽略吧，难复现&难排查
        // https://app.asana.com/1/45778247595893/project/1200987305187523/task/1210094969855291?focus=true
        'TCPlayer: ERROR: (CODE:',
        // 怀疑是某个用户的 qq 浏览器的插件问题，先忽略它
        'Error during distillation: TypeError: Illegal invocation e.stack:TypeError: Illegal invocation',
        // 可能是用户 浏览器插件 注入了 Veeva CLM 相关代码，先忽略
        "undefined is not an object (evaluating 'com.veeva.clm.updateCurrentRecordsOnExit')",
        "undefined is not an object (evaluating 'com.veeva.clm.updateRecordsOnExit')",
        // WeakRef 在某些环境下不支持，且无法完美 polyfill, 原因：弱引用特性依赖 GC，JS 层面无法模拟. 先忽略
        'WeakRef is not defined',
        'Script execution context has stopped',
        'Network Error',
        'Request aborted',
        'insertHotWords is not defined',
        'Attempt to delete range from database without an in-progress transaction',
        'An internal error was encountered in the Indexed Database server',
        'Database deleted by request of the user',
        'NetworkError when attempting to fetch resource',
        'timeout of 0ms exceeded',
        'Connection to Indexed Database server lost',
        'The index is not in the allowed range',
        'Encountered full disk while opening backing store',
        'saveQuiz',
        'Internal error when calculating storage usage',
        'A network error occurred',
        'UC_shouldChangeTheme'
    ].concat(dynamicSentryIgnoreErrors);
    if (isPageReloading) {
        return null;
    }

    if (window.location.protocol === 'file:') {
        console.warn('[Sentry] 忽略 file:// 环境下的错误');
        return null; // 返回 null 会阻止上报
    }

    if (
        event !== undefined &&
        event.exception !== undefined &&
        event.exception.values !== undefined &&
        event.exception.values.length == 1
    ) {
        const e = event.exception.values[0] || {};
        const exceptionMessage = e.value || '';
        const stacktrace = e.stacktrace;

        if (
            e.type === 'UnhandledRejection' &&
            exceptionMessage.indexOf('Non-Error promise rejection captured with value:') > -1
        ) {
            return null;
        }

        // 堆栈中包含 PDF.js 库相关的错误，忽略这些第三方库的内部错误
        const isFromPdfJs = stacktrace?.frames?.some((frame: any) => {
            if (!frame?.filename) {
                return false;
            }
            const filename = frame.filename;
            // 匹配 PDF.js 相关文件：路径中包含 pdf 且以 .js 结尾的文件
            return /\/pdf(?:\.[^\/]*)?\.js$/i.test(filename);
        });
        if (isFromPdfJs) {
            return null;
        }

        // 忽略 [object Event] 错误
        if (e.type === 'Event') {
            return null;
        }
        // 忽略一些错误
        if (ignoreErrorList.some((error) => exceptionMessage.indexOf(error) > -1)) {
            return null;
        }
        // 忽略 HeyTap 浏览器的 err null 错误
        if (isHeyTapBrowser && exceptionMessage.indexOf('err null') > -1) {
            return null;
        }
        // 忽略 MobileLenovoBrowser 浏览器的 nativeInitedNotify is not defined 错误
        if (isMobileLenovoBrowser && exceptionMessage.indexOf('nativeInitedNotify is not defined') > -1) {
            return null;
        }
        // 忽略 PinganZhiniao webview 的 lifecycle is not defined错误
        if (isPinganZhiniaoWebview && exceptionMessage.indexOf('lifecycle is not defined') > -1) {
            return null;
        }
        if (
            exceptionMessage.indexOf('[android]:WebViewJavascriptBridge: WARNING: javascript handler threw.') > -1 ||
            exceptionMessage.indexOf('WebViewJavascriptBridge: WARNING: no handler for message from ObjC') > -1
        ) {
            return null;
        }
        if (isQQBrowser && exceptionMessage.indexOf('news_page') > -1) {
            return null;
        }
        // Can't find variable: func_1744872163092_4465
        if (isFeiShuMobile && exceptionMessage.indexOf("Can't find variable: func_") > -1) {
            return null;
        }
        if (exceptionMessage.indexOf('domtoimage: Error while reading CSS rules from') > -1) {
            return null;
        }
        if (isEdge && exceptionMessage.indexOf("Cannot read properties of null (reading 'rule')") > -1) {
            return null;
        }
        // https://app.asana.com/1/45778247595893/project/1203622320206294/task/1210094967797989?focus=true
        if (
            exceptionMessage.indexOf('Connection to Indexed Database server lost') > -1
        ) {
            return null;
        }
        if (exceptionMessage.indexOf('/js-sdk/collect-0.0.5.min.js') > -1) {
            return null;
        }
    }
    return event;
};
